import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import FilteringTable from './Table/FilteringTable/FilteringTable';
import ColumnFilter from './Table/FilteringTable/ColumnFilter';
import PageTitle from '../layouts/PageTitle';
import { tableColumnNamesReferee } from '../../utils/tableColumnNamesReferee';
import { arrayToFlatObject } from '../../utils/arrayToFlatObject';
import { tableRowsValuesRefereeAdministration } from '../../utils/tableRowsValuesRefereeAdministration';
import { CommonCollections } from '../../App';
import RefereeReportCell from './RefereeReportCell';
import { filteringTableGetTableColumns } from '../../utils/filteringTableGetTableColumns';
import { useStaticCollections } from '../../context/StaticCollections';
import { useRefereeCollections } from '../../context/RefereeReportCollections';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext';
import { useAdminActionCollections } from '../../context/AdminActionCollections';
import {
  CM_PRIMARY_ID,
  NOMINATION_STATUS,
  REFEREE_STATUS,
} from '../../utils/constants';

const Cell = ({ row }) => {
  const { setRefereeReportId } = useContext(CommonCollections);
  const { refereeStatusId, id } = row.original;
  const buttonIcon =
    refereeStatusId === REFEREE_STATUS.PENDING ||
    refereeStatusId === REFEREE_STATUS.EXTENSION_APPROVED ||
    refereeStatusId === REFEREE_STATUS.EXTENSION_REQUESTED
      ? 'bi bi-pen action-button'
      : 'bi bi-eye view-button';
  const buttonToolTipText =
    refereeStatusId === REFEREE_STATUS.PENDING ||
    refereeStatusId === REFEREE_STATUS.EXTENSION_APPROVED ||
    refereeStatusId === REFEREE_STATUS.EXTENSION_REQUESTED
      ? 'Edit'
      : 'View';
  const linkUrl = `/referee`;

  return (
    <Link
      to={linkUrl}
      onClick={() => {
        sessionStorage.setItem('refereeReportId', id);
        setRefereeReportId(id);
      }}
    >
      <span title={buttonToolTipText}>
        <i className={buttonIcon} />
      </span>
    </Link>
  );
};

const RefereeReport = () => {
  const { userDetails } = useLoggedInUserContextProvider();
  const {
    fellows,
    declineReasons,
    refereeStatuses,
    sectionalCommitteesWithCmse,
    users,
    nonAdminNominationStatusTypes,
    nonAasReferees,
    isLoading: staticLoading,
  } = useStaticCollections();

  const { setRefereeReportId } = useContext(CommonCollections);
  const {
    refereeReports,
    candidates,
    nominationArray,
    nonAdminRefereeReportId,
    setNonAdminRefereeReportId,
    isLoading: refereeLoading,
  } = useRefereeCollections();

  const { enableRefereeReportUploads } = useAdminActionCollections();
  const [refereesData, setRefereesData] = useState([]);
  const [tableColumn, setTableColumn] = useState([]);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');

  useEffect(() => {
    if (userDetails) {
      const tableColumns = filteringTableGetTableColumns(
        tableColumnNamesReferee,
        ColumnFilter,
        RefereeReportCell,
      );

      const tableData = tableRowsValuesRefereeAdministration(
        refereeReports,
        refereeStatuses,
        declineReasons,
      );

      setTableColumn(tableColumns);
      const refereeData = [
        tableData?.map((item) => item.nominationId),
        tableData,
      ];
      const nominationArr = nominationArray.map((item) => ({
        [item.id]: item.attributes,
      }));

      const nominationsObj = arrayToFlatObject(nominationArr);

      const tableDataTwo = Object.values(refereeData[1]).map((referee) => {
        if (
          nominationsObj[referee.nominationId] &&
          (nominationsObj[referee.nominationId]?.nominationStatusTypeId ===
            NOMINATION_STATUS.SHORTLISTED ||
            nominationsObj[referee.nominationId]?.nominationStatusTypeId ===
              NOMINATION_STATUS.SUBMITTED ||
            nominationsObj[referee.nominationId]?.nominationStatusTypeId ===
              NOMINATION_STATUS.RECOMMENDED)
        ) {
          referee.candidate =
            candidates[nominationsObj[referee.nominationId].candidateId];
          referee.sectionalCommittee =
            sectionalCommitteesWithCmse[
              nominationsObj[referee.nominationId]?.sectionalCommitteeId ??
                CM_PRIMARY_ID
            ];
          referee.refereeUser = referee.refereeFellowId
            ? fellows[referee.refereeFellowId]
            : nonAasReferees[referee.nonAasRefereeId];
        }

        return referee;
      });

      const filteredTableDataTwo = tableDataTwo.filter(
        (referee) => referee.candidate,
      );

      filteredTableDataTwo.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
      );

      if (!enableRefereeReportUploads) {
        setNotificationMessage(
          'Referee report submissions are now closed. If you are required to submit a report, please contact fellowship@science.org.au.',
        );
        setNotificationTitle('Referee report submissions are now closed');
      }

      setRefereesData(filteredTableDataTwo);
    }
  }, [
    refereeStatuses,
    sectionalCommitteesWithCmse,
    users,
    declineReasons,
    fellows,
    candidates,
    nominationArray,
    refereeReports,
    setRefereeReportId,
    userDetails,
    nonAdminNominationStatusTypes,
    nonAasReferees,
    nonAdminRefereeReportId,
    setNonAdminRefereeReportId,
    enableRefereeReportUploads,
  ]);

  const resetNotification = useCallback(() => {
    setNotificationTitle('');
    setNotificationMessage('');
  }, []);

  useEffect(() => {
    if (enableRefereeReportUploads) {
      resetNotification();
    }
  }, [enableRefereeReportUploads, resetNotification]);

  return (
    <>
      <PageTitle
        activeMenu="My References"
        motherMenu="Home"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
      />
      <FilteringTable
        componentName="RefereeReport"
        columns={tableColumn}
        data={refereesData}
        markDownName="myReferences"
        isLoading={staticLoading || refereeLoading}
      />
    </>
  );
};

Cell.propTypes = {
  row: PropTypes.object,
};

export default RefereeReport;
